import { UserRoundCog } from "lucide-react";

import { AbsenceBadge } from "../../../components/ui/AbsenceBadge";
import { Table } from "../../../components/ui/Table";
import { CustomerDetailsController } from "../../../services/controllers/customer";
import { formatDateShort, formatTutorName } from "../../../services/functions";

import { formatBrDate } from "../../../services/formatResponses/date";

import { PetRowActions } from "./PetRowActions";

const membershipColors = {
  ABSENT: "bg-orange-50 text-orange-700 border-orange-200",
  NO_PRODUCT: "bg-blue-50 text-blue-700 border-blue-200",
  NO_ADDITIONAL: "bg-violet-50 text-violet-700 border-violet-200",
  POTENTIAL_SUBSCRIBER: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const subscriptionLabels = {
  ACTIVE: { label: "Sim", className: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  FORMER: { label: "Não", className: "border-gray-200 bg-gray-50 text-gray-600" },
  // FORMER: { label: "Ex-assinante", className: "border-amber-200 bg-amber-50 text-amber-700" },
  NEVER: { label: "Não", className: "border-gray-200 bg-gray-50 text-gray-600" },
};

function DateValue({ value, emptyLabel = "Nunca" }) {
  return (
    <span className={value ? "text-gray-700" : "text-gray-400"}>
      {value ? formatDateShort(value.split("T")[0]) : emptyLabel}
    </span>
  );
}

function SubscriptionValue({ status }) {
  const config = subscriptionLabels[status] || subscriptionLabels.NEVER;
  return (
    <span className={`rounded-full border px-2 py-1 text-xs font-semibold ${config.className}`}>
      {config.label}
    </span>
  );
}

function OpportunityTypes({ memberships }) {
  if (!memberships.length) return <span className="text-gray-400">-</span>;

  return (
    <div className="flex flex-wrap gap-1 px-1 py-2">
      {memberships.map((membership) => (
        <span
          key={membership.membershipId}
          className={`rounded-full border px-2 py-1 text-[11px] font-semibold ${membershipColors[membership.ruleCode] || "border-gray-200 bg-gray-50 text-gray-700"}`}
          title={`${membership.label} · ${membership.contactCount} contato(s)`}
        >
          {membership.label}
        </span>
      ))}
    </div>
  );
}

function ContactValue({ contactSummary }) {
  const contacted = contactSummary.status === "CONTACTED";
  return (
    <div className="flex w-full flex-col gap-1 px-1 py-2 text-xs">
      <span className={contacted ? "font-semibold text-emerald-600" : "text-gray-500"}>
        {contactSummary.partial ? "Parcial" : contacted ? "Realizado" : "Pendente"}
      </span>
      {contactSummary.latestContactAt && (
        <span className="text-[11px] text-gray-400">{formatBrDate(contactSummary.latestContactAt)}</span>
      )}
    </div>
  );
}

function renderCell(column, row, customerDetailsController) {
  const pet = row.pet;

  if (column.key === "tutor") {
    return (
      <button
        type="button"
        className="flex items-center gap-1 border-b border-transparent font-medium hover:border-[#524a4a]"
        disabled={!row.customerId}
        onClick={() => row.customerId && customerDetailsController.open(row.customerId)}
      >
        <UserRoundCog aria-hidden="true" size={16} />
        {formatTutorName(row.customerName)}
      </button>
    );
  }

  if (column.key === "pet") {
    return (
      <span className={pet.blocked ? "text-gray-400 line-through" : "text-gray-700"}>
        {pet.petName || "Sem nome"}
      </span>
    );
  }
  if (column.key === "type") return <OpportunityTypes memberships={row.memberships} />;
  if (column.key === "absence") return <AbsenceBadge days={row.activity.absenceDays} />;
  if (column.key === "lastAppointment") return <DateValue value={row.activity.lastAppointmentAt} />;
  if (column.key === "nextAppointment") return <DateValue value={row.activity.nextAppointmentAt} emptyLabel="Sem agenda" />;
  if (column.key === "subscriber") return <SubscriptionValue status={row.subscription.status} />;
  if (column.key === "frequency") {
    return row.subscription.frequencyPerMonth > 0
      ? <span className="font-semibold text-sky-600">{row.subscription.frequencyPerMonth}x/mês</span>
      : <span className="text-gray-400">-</span>;
  }
  if (column.key === "lastSubscription") return <DateValue value={row.subscription.lastSubscriptionAt} />;
  if (column.key === "contact") return <ContactValue contactSummary={row.contactSummary} />;
  if (column.key === "actions") return <PetRowActions row={row} />;
  if (column.key.startsWith("category:")) {
    const categoryCode = column.key.replace("category:", "");
    return <DateValue value={row.categoryActivity[categoryCode]?.lastAt} />;
  }

  return <span className="text-gray-400">-</span>;
}

export function PetRow({ columns, row }) {
  const CCustomerDetails = CustomerDetailsController();

  return (
    <Table.Line>
      {columns.map((column) => (
        <Table.Item key={column.key}>
          {renderCell(column, row, CCustomerDetails)}
        </Table.Item>
      ))}
    </Table.Line>
  );
}
